import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Grid, Button, TextField, Typography, TableCell, TableRow, Box,
  TableBody, Table, TableHead, TableContainer, TablePagination, OutlinedInput, Checkbox, createFilterOptions,
  FormControl, InputLabel, MenuItem, Select, Autocomplete, InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getClientList, getCamerasList, getFalseEventsReport, getTotalEvents } from 'src/api/EventReports/EventReports';
import { getClientProperties } from 'src/api/ShiftMaster';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Iconify from 'src/components/Iconify';
import SearchNotFound from 'src/components/SearchNotFound';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import * as XLSX from 'xlsx';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';

import Page from 'src/components/Page';

const iconCamera = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIconCamera = <CheckBoxIcon fontSize="small" />;

const iconClient = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIconClient = <CheckBoxIcon fontSize="small" />;

const SELECT_ALL_DISPOSITIONS_VALUE = '__select_all_dispositions__';
const SELECT_ALL_CLIENTS_VALUE = '__select_all_clients__';

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  marginLeft: 15,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`,
  },
}));

const getCameraGroupKey = (row) =>
  [
    row?.displayname,
    row?.Location,
    row?.deviceid,
    row?.devicename,
  ].map((value) => String(value ?? '').trim()).join('|');

const getEventTypeName = (eventType) => {
  const eventTypeName = String(eventType || '').trim();
  return eventTypeName || '-';
};

const buildGroupedFalseEvents = (rows) => {
  const groupedRows = new Map();

  (rows || []).forEach((row) => {
    const groupKey = getCameraGroupKey(row);
    const eventType = getEventTypeName(row?.eventType );
    const eventCount = Number(row?.false_event_count ?? 0);

    if (!groupedRows.has(groupKey)) {
      groupedRows.set(groupKey, {
        displayname: row?.displayname || '',
        Location: row?.Location || '',
        deviceid: row?.deviceid || '',
        devicename: row?.devicename || '',
        dispositions: [],
      });
    }

    const groupedRow = groupedRows.get(groupKey);
    const existingDisposition = groupedRow.dispositions.find(
      (disposition) => disposition.eventType === eventType
    );

    if (existingDisposition) {
      existingDisposition.count += Number.isNaN(eventCount) ? 0 : eventCount;
    } else {
      groupedRow.dispositions.push({
        eventType,
        count: Number.isNaN(eventCount) ? 0 : eventCount,
      });
    }
  });

  return Array.from(groupedRows.values());
};

const normalizeDisplayName = (name) =>
  String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const buildUniqueClientOptions = (clients) => {
  const grouped = new Map();

  (clients || []).forEach((clientItem) => {
    const key = normalizeDisplayName(clientItem?.displayname);
    if (!key) return;

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key).push(clientItem);
  });

  return Array.from(grouped.values()).map((groupItems) => {
    if (groupItems.length === 1) return groupItems[0];

    const assignTargets = new Set(
      groupItems
        .map((item) => Number(item.AssignClient || 0))
        .filter((assignId) => assignId > 0)
    );

    const mappedMain = groupItems.find((item) => assignTargets.has(Number(item.ID)));
    if (mappedMain) return mappedMain;

    const rootEntry = groupItems.find((item) => Number(item.AssignClient || 0) === 0);
    return rootEntry || groupItems[0];
  });
};

const FalseEvents = () => {
  const [filterName, setFilterName] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [error, setError] = useState(false);
  const [selectEvent, setSelectEvent] = useState([]);
  const [clientInfo, setClientInfo] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [selectedClientCameras, setSelectedClientCameras] = useState([]);
  const [events, setEvents] = useState([]);
  const eventSelectRef = useRef(null);
  const [eventMenuWidth, setEventMenuWidth] = useState();

  const [checkClientValue, setCheckClientValue] = useState(false);
  const [openClientAuto, setOpenClientAuto] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientLocationOptions, setClientLocationOptions] = useState([]);
  const [selectedClientLocations, setSelectedClientLocations] = useState([]);
  const [clientsWithoutLocations, setClientsWithoutLocations] = useState([]);
  const [locationCheckValue, setLocationCheckValue] = useState(false);
  const locationRequestRef = useRef(0);
  const cameraListRequestRef = useRef(false);

  const [checkCameraValue, setCheckCameraValue] = useState(false);
  const [activeCameras, setActiveCameras] = useState([]);
  const [openCameraAuto, setOpenCameraAuto] = useState(false);
  const [cameraListLoading, setCameraListLoading] = useState(false);
  const [camerasLoaded, setCamerasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCameras = useCallback(() => {
    if (cameraListRequestRef.current) {
      return;
    }

    cameraListRequestRef.current = true;
    setCameraListLoading(true);

    getCamerasList((response) => {
      cameraListRequestRef.current = false;
      setCameraListLoading(false);
      setCamerasLoaded(true);

      if (response.status === 200) {
        setCameras(response.data || []);
      }
    });
  }, []);

  useEffect(() => {
    getClientList((response) => {
      if (response.status === 200) {
        const usClients = response.data.filter(
          (row) => row.ClientType === 'US' && row.Type === null
        );

        const sortedData = [...usClients].sort((a,b)=>
          a.displayname.trim().localeCompare(b.displayname.trim())
        );

        setClientInfo(buildUniqueClientOptions(sortedData));
      }
    });
    loadCameras();
    getTotalEvents((response) => {
      if (response.status === 200) {
        setEvents(response.data);
      }
    });
  }, [loadCameras]);

  const normalizeLocationOptions = (locations, parentClientId) =>
    Array.from(
      new Map(
        (Array.isArray(locations) ? locations : locations ? [locations] : [])
          .map((locationItem) => ({
            ...locationItem,
            ID: Number(locationItem?.ID ?? locationItem?.id ?? 0),
            Location: String(locationItem?.Location || locationItem?.name || "").trim(),
            parentClientId: Number(parentClientId || 0),
          }))
          .filter((locationItem) => locationItem.ID > 0)
          .map((locationItem) => [`${locationItem.parentClientId}-${locationItem.ID}`, locationItem])
      ).values()
    );

  const getClientId = (clientItem) =>
    Number(clientItem?.ID ?? clientItem?.Id ?? clientItem?.id ?? 0);

  const getLocationId = (locationItem) =>
    Number(locationItem?.ID ?? locationItem?.id ?? 0);

  const getCameraClientId = (camera) =>
    Number(camera?.clientid ?? camera?.clientId ?? camera?.client_id ?? camera?.ClientId ?? 0);

  const uniqueClientOptions = buildUniqueClientOptions(clientInfo || []);

  const getScopedClientIds = useCallback((
    clientItems = selectedClients,
    selectedLocationIds = selectedClientLocations,
    locationOptions = clientLocationOptions,
    noLocationClientIds = clientsWithoutLocations
  ) => {
    const selectedLocationIdSet = new Set(
      selectedLocationIds.map((locationId) => Number(locationId))
    );
    const noLocationClientIdSet = new Set(
      noLocationClientIds.map((clientId) => Number(clientId))
    );

    const scopeIds = clientItems.flatMap((clientItem) => {
      const clientId = getClientId(clientItem);
      const mappedLocations = locationOptions.filter(
        (locationItem) => Number(locationItem.parentClientId) === clientId
      );

      if (!mappedLocations.length) {
        return noLocationClientIdSet.has(clientId) ? [clientId] : [];
      }

      return mappedLocations
        .map(getLocationId)
        .filter((locationId) => selectedLocationIdSet.has(locationId));
    });

    return [...new Set(scopeIds.filter(Boolean))];
  }, [selectedClients, selectedClientLocations, clientLocationOptions, clientsWithoutLocations]);

  const getClientPayloadValue = () => {
    if (!selectedClients.length) {
      return "";
    }

    return getScopedClientIds().join(",");
  };

  const getDispositionPayloadValue = () => selectEvent.join(",");

  const getCameraPayloadValue = () => {
    if (activeCameras.length > 0) {
      return activeCameras.join(',');
    }

    return "";
  };

  useEffect(() => {
    if (selectedClients.length === 0) {
      setSelectedClientCameras([]);
      return;
    }

    const scopedClientIds = getScopedClientIds();

    if (!scopedClientIds.length) {
      setSelectedClientCameras([]);
      return;
    }

    const scopedClientIdSet = new Set(scopedClientIds.map((clientId) => Number(clientId)));
    const filteredCameras = cameras.filter((camera) =>
      scopedClientIdSet.has(getCameraClientId(camera))
    );

    setSelectedClientCameras(filteredCameras);
  }, [
    selectedClients,
    selectedClientLocations,
    clientLocationOptions,
    cameras,
    getScopedClientIds,
  ]);

  useEffect(() => {
    const validCameraIds = new Set(
      selectedClientCameras.map((camera) => camera.deviceid)
    );

    setActiveCameras((prevActiveCameras) =>
      prevActiveCameras.filter((cameraId) => validCameraIds.has(cameraId))
    );
  }, [selectedClientCameras]);

  const handleButtonClick = () => {
    setData([]);
    setError(false);

    if (!fromDate || !toDate) {
      setError(true);
    }
    if (selectEvent.length === 0) {
      setError(true);
    }

    if (fromDate && toDate && selectEvent.length > 0) {
      setLoading(true);
      
      const data = {
        from_date: dayjs(fromDate).format('YYYY-MM-DD'),
        to_date: dayjs(toDate).format('YYYY-MM-DD'),
        client_id: getClientPayloadValue(),
        event_type: getDispositionPayloadValue(),
        camera_id: getCameraPayloadValue(),
      };

      getFalseEventsReport(data, (response) => {
        if (response.status === 200 && response.data != null) {
          setData(response.data);
        }
        setLoading(false);
      });
    }
  };

  const handleClientFilterOptions = (options, params) => {
    const filter = createFilterOptions();
    const filtered = filter(options, params);
    
    const selectedOptions = filtered.filter((option) =>
      selectedClients.some((selectedClientItem) => selectedClientItem.ID === option.ID)
    );
    const unselectedOptions = filtered.filter((option) =>
      !selectedClients.some((selectedClientItem) => selectedClientItem.ID === option.ID)
    );
    
    const sortedOptions = [...selectedOptions, ...unselectedOptions];
  
    return [{ title: 'Select All', selectall: true, ID: SELECT_ALL_CLIENTS_VALUE }, ...sortedOptions];
  };

  const handleClientChange = (event, value) => {
    let nextSelectedClients = [];

    if (value.some((obj) => obj.hasOwnProperty('selectall'))) {
      nextSelectedClients = checkClientValue ? [] : uniqueClientOptions;
    } else {
      nextSelectedClients = value;
    }

    setSelectedClients(nextSelectedClients);
    setCheckClientValue(
      uniqueClientOptions.length > 0 && nextSelectedClients.length === uniqueClientOptions.length
    );
    setSelectedClientLocations([]);
    setLocationCheckValue(false);
    setActiveCameras([]);
    setCheckCameraValue(false);
    setClientLocationOptions([]);
    setClientsWithoutLocations([]);
    loadCameras();

    if (!nextSelectedClients.length) {
      locationRequestRef.current += 1;
      return;
    }

    const currentRequestId = locationRequestRef.current + 1;
    locationRequestRef.current = currentRequestId;

    Promise.all(
      nextSelectedClients.map(
        (selectedClientItem) =>
          new Promise((resolve) => {
            getClientProperties({ AssignClient: String(selectedClientItem.ID) }, (response) => {
              const clientId = getClientId(selectedClientItem);
              if (response.status === 200) {
                resolve({
                  clientId,
                  locations: normalizeLocationOptions(response.data, clientId),
                });
                return;
              }
              resolve({ clientId, locations: [] });
            });
          })
      )
    ).then((locationGroups) => {
      if (locationRequestRef.current !== currentRequestId) {
        return;
      }

      const normalizedLocations = locationGroups.flatMap((locationGroup) => locationGroup.locations);
      const noLocationClientIds = locationGroups
        .filter((locationGroup) => locationGroup.locations.length === 0)
        .map((locationGroup) => locationGroup.clientId);

      setClientLocationOptions(normalizedLocations);
      setClientsWithoutLocations(noLocationClientIds);
    });
  };

  useEffect(() => {
    setCheckClientValue(
      uniqueClientOptions.length > 0 && selectedClients.length === uniqueClientOptions.length
    );
  }, [selectedClients, uniqueClientOptions.length]);

  useEffect(() => {
    setLocationCheckValue(
      clientLocationOptions.length > 0 &&
      selectedClientLocations.length === clientLocationOptions.length
    );
  }, [clientLocationOptions, selectedClientLocations]);

  const handleLocationFilterOptions = (options, params) => {
    const filter = createFilterOptions();
    const filtered = filter(options, params);
    return options.length > 0 ? [{ title: 'Select All', selectall: true }, ...filtered] : filtered;
  };

  const handleLocationChange = (event, value) => {
    let nextSelectedLocations = [];

    if (value.some((obj) => obj.hasOwnProperty('selectall'))) {
      nextSelectedLocations = locationCheckValue
        ? []
        : clientLocationOptions.map((locationItem) => locationItem.ID || locationItem.id);
    } else {
      nextSelectedLocations = value.map((locationItem) => locationItem.ID || locationItem.id);
    }

    setSelectedClientLocations(nextSelectedLocations);
    setLocationCheckValue(
      clientLocationOptions.length > 0 && nextSelectedLocations.length === clientLocationOptions.length
    );
  };

  const handleCameraChange = (event, value) => {
    let actCameras = [];

    if (value.some(obj => obj.hasOwnProperty('selectall'))) {
      if (activeCameras.length === selectedClientCameras.length) {
        setActiveCameras([]);
        setCheckCameraValue(false);
      } else {
        selectedClientCameras.forEach(item => {
          actCameras.push(item.deviceid);
        });
        setActiveCameras(actCameras);
        setCheckCameraValue(true);
      }
    } else {
      const cameraIds = value.map(cam => cam.deviceid);
      setActiveCameras(cameraIds);
      setCheckCameraValue(cameraIds.length === selectedClientCameras.length);
    }
  };

  useEffect(() => {
    setCheckCameraValue(
      selectedClientCameras.length > 0 && activeCameras.length === selectedClientCameras.length
    );
  }, [activeCameras, selectedClientCameras]);

  const handleCameraFilterOptions = (options, params) => {
    const filter = createFilterOptions();
    const filtered = filter(options, params);
    
    const selectedOptions = filtered.filter(option => activeCameras.includes(option.deviceid));
    const unselectedOptions = filtered.filter(option => !activeCameras.includes(option.deviceid));
    const sortedOptions = [...selectedOptions, ...unselectedOptions];
  
    return options.length > 0 ? [{ title: 'Select All', selectall: true }, ...sortedOptions] : filtered;
  };

  const handleEventChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    const allDispositionIds = events.map((eventItem) => String(eventItem.Id));

    if (selectedValues.includes(SELECT_ALL_DISPOSITIONS_VALUE)) {
      const nextEvents = selectEvent.length === events.length ? [] : allDispositionIds;
      setSelectEvent(nextEvents);

      if (nextEvents.length > 0) {
        setError(false);
      }
      return;
    }

    setSelectEvent(selectedValues);
  
    if (selectedValues.length > 0) {
      setError(false);
    }
  };

  const handleEventMenuOpen = () => {
    setEventMenuWidth(eventSelectRef.current?.clientWidth);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value.trim());
  };

  const columns = [
    { id: 'Sno', name: 'S.No' },
    { id: 'displayname', name: 'Client Name' },
    { id: 'Location', name: 'Location' },
    { id: 'devicename', name: 'Camera Name' },
    { id: 'cameraID', name: 'Camera Id' },
    { id: 'event_type', name: 'Disposition' },
    { id: 'false_event_count', name: 'Count' },
  ];

  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const groupedData = useMemo(() => buildGroupedFalseEvents(data), [data]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredData = groupedData.filter((item) => (
    (item.displayname && item.displayname.toLowerCase().includes(filterName.toLowerCase())) ||
    (item.Location && item.Location.toLowerCase().includes(filterName.toLowerCase())) ||
    (item.devicename && item.devicename.toLowerCase().includes(filterName.toLowerCase())) ||
    (item.deviceid && String(item.deviceid).includes(filterName)) ||
    item.dispositions.some((disposition) =>
      disposition.eventType.toLowerCase().includes(filterName.toLowerCase())
    )
  ));

  const isDataNotFound = filteredData.length === 0;

  const exportToExcel = () => {
    if (filteredData.length) {
      const exportData = [];
      filteredData.forEach((item, index) => {
        item.dispositions.forEach((disposition, dispIndex) => {
          exportData.push({
            'S.No': dispIndex === 0 ? index + 1 : '',
            'Client Name': dispIndex === 0 ? item.displayname : '',
            'Location': dispIndex === 0 ? item.Location : '',
            'Camera Name': dispIndex === 0 ? item.devicename : '',
            'Camera Id': dispIndex === 0 ? item.deviceid : '',
            'Disposition': disposition.eventType,
            'Count': disposition.count
          });
        });
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const filename = "False_Events.xlsx";

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "False Events");
      XLSX.writeFile(wb, filename);
    } else {
      alert("No data to Export.");
    }
  };

  const isAllDispositionsSelected = events.length > 0 && selectEvent.length === events.length;
  const showLocationDropdown = Boolean(selectedClients.length > 0 && clientLocationOptions.length > 0);
  const filterColumnSize = showLocationDropdown ? 2 : 3;

  return (
    <Page title="False Events">
      <Grid sx={{ marginLeft: '1rem' }}>
        <Grid container spacing={2} alignItems="center" sx={{ marginTop: '0.2rem' }}>
          <Grid item xs={12} sm={filterColumnSize}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label="From Date"
                  id="from-date"
                  format="YYYY-MM-DD"
                  value={fromDate}
                  onChange={(date) => setFromDate(date)}
                  maxDate={dayjs()}
                />
              </LocalizationProvider>
            </FormControl>
            {error && !fromDate && <Typography variant="body2" color="error">Select From Date</Typography>}
          </Grid>
          <Grid item xs={12} sm={filterColumnSize}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label="To Date"
                  id="to-date"
                  format="YYYY-MM-DD"
                  value={toDate}
                  onChange={(date) => setToDate(date)}
                  minDate={fromDate}
                  maxDate={dayjs()}
                />
              </LocalizationProvider>
            </FormControl>
            {error && !toDate && <Typography variant="body2" color="error">Select To Date</Typography>}
          </Grid>

          <Grid item xs={12} sm={filterColumnSize} sx={{ height: '100%' }}>
            <Autocomplete
              multiple
              fullWidth
              id="client"
              options={uniqueClientOptions}
              value={selectedClients}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              getOptionLabel={(option) => option.selectall ? 'Select All' : `${option.displayname || ''}`}
              onChange={handleClientChange}
              disableCloseOnSelect={openClientAuto}
              open={openClientAuto}
              onOpen={() => setOpenClientAuto(true)}
              onClose={() => setOpenClientAuto(false)}
              renderTags={() => null}
              filterOptions={handleClientFilterOptions}
              renderOption={(props, option) => {
                const key = option.selectall ? 'select-all-clients' : `client-${option.ID}`;
                return (
                  <li {...props} key={key}>
                    {option.selectall ? (
                      <>
                        <Checkbox
                          icon={iconClient}
                          checkedIcon={checkedIconClient}
                          style={{ marginRight: 8 }}
                          checked={checkClientValue}
                        />
                        {option.title}
                      </>
                    ) : (
                      <>
                        <Checkbox
                          icon={iconClient}
                          checkedIcon={checkedIconClient}
                          style={{ marginRight: 8 }}
                          checked={selectedClients.some((selectedClientItem) => selectedClientItem.ID === option.ID)}
                        />
                        {option.displayname}
                      </>
                    )}
                  </li>
                );
              }}
              renderInput={(params) => 
                <TextField {...params} label="Client" onFocus={() => setOpenClientAuto(true)} />
              }
            />
          </Grid>

          {showLocationDropdown && (
            <Grid item xs={12} sm={filterColumnSize}>
              <Autocomplete
                multiple
                fullWidth
                id="locations"
                options={clientLocationOptions}
                value={clientLocationOptions.filter((locationItem) =>
                  selectedClientLocations.includes(locationItem.ID || locationItem.id)
                )}
                getOptionLabel={(option) => option.selectall ? option.title : `${option.Location || ''}`}
                onChange={handleLocationChange}
                disableCloseOnSelect
                filterOptions={handleLocationFilterOptions}
                renderTags={() => null}
                renderOption={(props, option) => (
                  <li {...props} key={option.selectall ? 'select-all-locations' : `${option.parentClientId}-${option.ID}`}>
                    {option.selectall ? (
                      <>
                        <Checkbox
                          icon={iconClient}
                          checkedIcon={checkedIconClient}
                          style={{ marginRight: 8 }}
                          checked={locationCheckValue}
                        />
                        {option.title}
                      </>
                    ) : (
                      <>
                        <Checkbox
                          icon={iconClient}
                          checkedIcon={checkedIconClient}
                          style={{ marginRight: 8 }}
                          checked={selectedClientLocations.includes(option.ID || option.id)}
                        />
                        {option.Location}
                      </>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Locations" placeholder="Select Locations" />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={filterColumnSize}>
            <Autocomplete
              multiple
              fullWidth
              id="camera"
              options={selectedClientCameras}
              value={selectedClientCameras.filter(cam => activeCameras.includes(cam.deviceid))}
              loading={cameraListLoading && selectedClients.length > 0}
              loadingText="Loading cameras..."
              noOptionsText={
                selectedClients.length === 0
                  ? 'Select client cameras'
                  : !camerasLoaded || cameraListLoading
                    ? 'Loading cameras...'
                    : 'No cameras found'
              }
              getOptionLabel={(option) => {
                return option.selectall
                  ? 'Select All'
                  : `${option.deviceid || ''} ${option.devicename || option.CameraName || ''}`;
              }}
              onChange={handleCameraChange}
              disableCloseOnSelect={openCameraAuto}
              open={openCameraAuto}
              onOpen={() => setOpenCameraAuto(true)}
              onClose={() => setOpenCameraAuto(false)}
              isOptionEqualToValue={(option, value) => String(option.deviceid) === String(value.deviceid)}
              filterOptions={handleCameraFilterOptions}
              renderTags={() => null}
              renderOption={(props, option) => {
                const key = option.selectall ? 'select-all-cameras' : `camid-${option.deviceid}`;
                return (
                  <li {...props} key={key}>
                    {option.selectall && selectedClientCameras.length > 0 ? (
                      <>
                        <Checkbox
                          icon={iconCamera}
                          checkedIcon={checkedIconCamera}
                          style={{ marginRight: 8 }}
                          checked={checkCameraValue}
                        />
                        {option.title}
                      </>
                    ) : option.deviceid ? (
                      <>
                        <Checkbox
                          icon={iconCamera}
                          checkedIcon={checkedIconCamera}
                          style={{ marginRight: 8 }}
                          checked={activeCameras.includes(option.deviceid)}
                        />
                        {option.devicename || option.CameraName || ''}
                      </>
                    ) : ""}
                  </li>
                );
              }}
              renderInput={(params) => 
                <TextField {...params} label="Camera" onFocus={() => setOpenCameraAuto(true)} />
              }
            />
          </Grid>

          <Grid item xs={12} sm={filterColumnSize} sx={{ height: '100%' }}>
            <FormControl fullWidth>
              <InputLabel id="event-label">Disposition</InputLabel>
              <Select
                ref={eventSelectRef}
                labelId="event-label"
                multiple
                value={selectEvent}
                onOpen={handleEventMenuOpen}
                onChange={handleEventChange}
                input={<OutlinedInput label="Disposition" />}
                renderValue={() => ''}
                error={error && selectEvent.length === 0}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 280,
                      width: eventMenuWidth || 'auto',
                    },
                  },
                }}
              >
                <MenuItem value={SELECT_ALL_DISPOSITIONS_VALUE} sx={{ whiteSpace: 'normal' }}>
                  <Checkbox
                    checked={isAllDispositionsSelected}
                    indeterminate={selectEvent.length > 0 && selectEvent.length < events.length}
                    size="small"
                  />
                  <Typography variant="body2">Select All</Typography>
                </MenuItem>
                {events.map((event) => (
                  <MenuItem key={event.Id} value={String(event.Id)} sx={{ whiteSpace: 'normal' }}>
                    <Checkbox checked={selectEvent.includes(String(event.Id))} size="small" />
                    <Typography variant="body2">{event.EventType}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {error && selectEvent.length === 0 && (
              <Typography variant="body2" color="error">
                Select Disposition
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={filterColumnSize} sx={{ marginTop: { xs: 0, sm: 2 } }}>
            <Button variant="contained" onClick={handleButtonClick}>
              Submit
            </Button>
            <Button variant="contained" onClick={exportToExcel} sx={{ marginLeft: 1 }}>
              <InsertDriveFileIcon sx={{ marginRight: '5px' }} /> Export <SaveAltIcon sx={{ paddingLeft: '5px' }} />
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ marginTop: 2 }}>
          <SearchStyle
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Search"
            startAdornment={
              <InputAdornment position="start">
                <Iconify
                  icon="eva:search-fill"
                  sx={{ color: 'text.disabled', width: 20, height: 20 }}
                />
              </InputAdornment>
            }
          />
        </Grid>

        <Grid
          container
          alignItems="center"
          sx={{
            marginTop: '1rem',
            pl: 0,
            pr: { xs: 1, sm: 2, md: 3 },
            boxSizing: 'border-box',
          }}
        >
          <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
            <Table stickyHeader sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f2f2f2' }}>
                  {columns.map((item) => (
                    <TableCell key={item.id}>
                      {item.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell align="center" colSpan={columns.length}>
                      <Box sx={{ py: 3 }}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).flatMap((row, index) =>
                    row.dispositions.map((disposition, dispIndex) => (
                      <TableRow key={`${row.deviceid}-${row.displayname}-${disposition.eventType}`}>
                        {dispIndex === 0 && (
                          <>
                            <TableCell align="left" rowSpan={row.dispositions.length}>
                              {page * rowsPerPage + index + 1}
                            </TableCell>
                            <TableCell align="left" rowSpan={row.dispositions.length}>
                              {row.displayname}
                            </TableCell>
                            <TableCell align="left" rowSpan={row.dispositions.length}>
                              {row.Location || '-'}
                            </TableCell>
                            <TableCell align="left" rowSpan={row.dispositions.length}>
                              {row.devicename}
                            </TableCell>
                            <TableCell align="left" rowSpan={row.dispositions.length}>
                              {row.deviceid}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="left">{disposition.eventType}</TableCell>
                        <TableCell align="left">{disposition.count}</TableCell>
                      </TableRow>
                    ))
                  )
                )}
              </TableBody>
              {(isDataNotFound && filterName && !loading) ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={columns.length}>
                      <Box sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : null}
              {(filteredData.length === 0 && !filterName && !loading) && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={columns.length}>
                      <Box sx={{ py: 3 }}>
                        <Typography variant="body1" align="center" style={{ fontWeight: 'bold' }}>
                          No Data
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Grid>

        <Grid sx={{ pl: 0, pr: { xs: 1, sm: 2, md: 3 } }}>
          <TablePagination
            rowsPerPageOptions={[100, 300, 500]}
            page={page}
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            component="div"
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Grid>
      </Grid>
    </Page>
  );
};

export default FalseEvents;
